import { useEffect, useState } from 'react';
import { ExportCsv, Toolbar, useGridApiContext, GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import Tooltip from '@mui/material/Tooltip';
import { Box, Button, Menu, MenuItem } from '@mui/material';
import FormatLineSpacingIcon from '@mui/icons-material/FormatLineSpacing';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import DensitySmallIcon from '@mui/icons-material/DensitySmall';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
// Order avoids a PageCard/BasicDataGrid <-> AssetGridColumnDefs circular-import bug.
import { PageCard } from '../../../../Compounds/CardWithTitle';
import { BasicDataGrid } from '../../../../Compounds/Table/BasicDataGrid';
import { DEFAULT_TABLE_STYLES } from '../../../../../utils/styleHelpers';
import { ComponentSpinner } from '../../../../Compounds/Loading/ComponentSpinner';
import { DefaultPaymentFrequencyExceptionGridColumnDefs } from '../../ColumnDefs/paymentFrequencyExceptionDefs';
import { CustomQuickFilter } from '../../../../Compounds/UpstackDataGrid/CustomQuickFilter';
import { PageLayout } from '../../../../PageLayout';
import { fileNameHelper } from '../../../../../utils/fileNameHelper';
import {
  paymentFrequencyExceptionList,
  PaymentFrequencyException,
  deletePaymentFrequencyException
} from '../../../../../Api/PaymentFrequencyException/PaymentFrequencyExceptionApi';
import { TextSpan } from '../../../../Atoms/Text';
import { Icon } from '../../../../Atoms/Icon';
import { NewRecordDialog } from './NewRecordDialog';

const CustomToolbar = () => {
  const apiRef = useGridApiContext();

  const [densityMenuAnchor, setDensityMenuAnchor] = useState<null | HTMLElement>(null);
  const handleDensityChange = (density: 'compact' | 'standard' | 'comfortable') => {
    apiRef.current.setDensity(density);
    setDensityMenuAnchor(null);
  };

  return (
    <Toolbar>
      <Box
        sx={{
          padding: '1rem 0rem 1rem 1rem',
          gap: 1,
          flexWrap: 'wrap',
          display: 'flex',
          alignItems: 'left',
          width: '100%'
        }}>
        <CustomQuickFilter />
        <Box sx={{ marginLeft: 'auto', display: 'flex', gap: 0, flexWrap: 'wrap' }}>
          <Tooltip
            title="Density"
            enterDelay={200}>
            <Button
              startIcon={<FormatLineSpacingIcon fontSize="small" />}
              onClick={(e) => setDensityMenuAnchor(e.currentTarget)}
              sx={{ fontSize: '.8rem', color: 'secondary.main' }}>
              Density
            </Button>
          </Tooltip>

          <Menu
            anchorEl={densityMenuAnchor}
            open={Boolean(densityMenuAnchor)}
            onClose={() => setDensityMenuAnchor(null)}>
            <MenuItem onClick={() => handleDensityChange('compact')}>
              <FormatAlignJustifyIcon
                fontSize="small"
                sx={{ marginRight: 1 }}
              />
              Compact
            </MenuItem>
            <MenuItem onClick={() => handleDensityChange('standard')}>
              <DensityMediumIcon
                fontSize="small"
                sx={{ marginRight: 1 }}
              />
              Standard
            </MenuItem>
            <MenuItem onClick={() => handleDensityChange('comfortable')}>
              <DensitySmallIcon
                fontSize="small"
                sx={{ marginRight: 1 }}
              />
              Comfortable
            </MenuItem>
          </Menu>

          <Tooltip
            title="Download as CSV"
            enterDelay={200}>
            <ExportCsv
              options={{ fileName: fileNameHelper('Payment Frequency Exceptions') }}
              render={
                <Button
                  startIcon={<FileDownloadIcon fontSize="small" />}
                  sx={{ fontSize: '.8rem', color: 'secondary.main' }}>
                  Export
                </Button>
              }
            />
          </Tooltip>
        </Box>
      </Box>
    </Toolbar>
  );
};

export const PaymentFrequencyExceptions = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<PaymentFrequencyException[]>([]);
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    mergeCustomerId: false,
    masterCustomerId: false,
    sfdcCustomerId: false
  });

  const loadData = async () => {
    setIsLoading(true);
    const { error, data } = await paymentFrequencyExceptionList();
    if (error) {
      setIsLoading(false);
      return;
    }
    if (data) {
      setRows(data);
    }
    setIsLoading(false);
  };

  const deleteRow = async (id: string) => {
    const { error } = await deletePaymentFrequencyException(id);
    if (error) {
      return;
    }
    loadData();
  };

  useEffect(() => {
    loadData();
  }, []);

  const sxStyle = {
    ...DEFAULT_TABLE_STYLES.sx,
    maxHeight: 'calc(100vh - 250px)',
    '.MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer': {
      marginLeft: 'auto'
    },
    borderWidth: '0',
    borderTopLeftRadius: '0',
    borderTopRightRadius: '0',
    borderBottomLeftRadius: '.75rem',
    borderBottomRightRadius: '.75rem'
  };

  const handleNewMap = () => {
    setIsFormOpen(true);
  };

  const cardActions = () => (
    <button
      className="flex items-center space-x-2"
      onClick={handleNewMap}
      data-cy="new-record-button"
      data-testid="new-record-button">
      <Icon type="plus" />
      <TextSpan
        color="indigo"
        size="sm14">
        New
      </TextSpan>
    </button>
  );

  return (
    <PageLayout
      className="w-full"
      dataTestId="merge-customer-list-page"
      dataCy="merge-customer-list-card"
      pageTitle="">
      <PageCard
        dataTestId="payment-frequency-exceptions-list"
        dataCy="payment-frequency-exceptions-list"
        childrenWrapperClass="max-w-[60vw] min-w-full"
        title="Payment Frequency Exceptions"
        cardActions={cardActions()}>
        <>
          {isLoading && (
            <div className="flex h-[10rem] m-auto">
              <ComponentSpinner label="Loading data..." />
            </div>
          )}
          {!isLoading && (
            <BasicDataGrid
              rows={rows}
              columns={DefaultPaymentFrequencyExceptionGridColumnDefs(deleteRow)}
              sxStyle={sxStyle}
              disableColumnMenu
              showColumnVerticalBorder
              showCellVerticalBorder
              slots={{ toolbar: CustomToolbar }}
              showToolbar
              slotProps={{ toolbar: { showSearch: true } }}
              primaryKey="id"
              hideFooterRowCount={false}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'supplierName', sort: 'asc' }]
                }
              }}
            />
          )}
          <NewRecordDialog
            open={isFormOpen}
            onClose={(refresh = false) => {
              setIsFormOpen(false);
              if (refresh) loadData();
            }}
          />
        </>
      </PageCard>
    </PageLayout>
  );
};
